/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

//import { useParams } from 'react-router';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-duotone-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Accordion, AccordionDetails, AccordionProps, AccordionSummary,
    accordionSummaryClasses,
    AccordionSummaryProps,
    AppBar,
    Box,
    CardMedia,
    Grid2, Link, List,
    ListItem, ListItemText, Paper, styled,
    Tooltip, Typography,
    typographyClasses
} from '@mui/material';
import { PageLayout, useIsMobile } from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

import { AppSettings } from '../AppSettings';
// import involvement from '../assets/img/ProspectiveInvolvement.jpg';
// import questionnaires from '../assets/img/ProspectiveQuestionnaires.jpg';
// import trialprocedures from '../assets/img/ProspectiveTrialProcedures.jpg';
// import usePicfs from '../hooks/data/usePicfs';



/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ITrialInformationPageProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const Paragraph = styled(Typography)(() => ({
    marginBottom: '1rem'
}))

const StyledListItemText = styled(ListItemText)(() => ({
    marginBottom: '1rem'
}))

const PaperContainer = styled(Paper)(({ theme }) => ({
    marginTop: '3rem',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    }
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.main,
    fontSize: '2.5rem',
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: '2rem',
    marginTop: '1.5rem',
    marginBottom: '1rem',
    fontWeight: 'bold'
}))

const StyledCardMedia = styled(CardMedia)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    height: '100%'
}))

const StyledCardMediaContainer = styled(Box)(() => ({
    width: '100%',
    paddingTop: '56.25%',
    position: 'relative'
}))

const StyledQuickAccess = styled(AppBar)(() => ({
    display: 'flex',
    backgroundColor: 'white'
}))

const StyledQuickAccessHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    padding: '1rem',
    fontSize: '1.25rem'
}))

const StyledQuickAccessBody = styled(Box)(({ theme }) => ({
    display: 'flex',
    backgroundColor: 'white',
    color: theme.palette.secondary.main,
    fontSize: '1.0rem',

    ...theme.applyStyles('dark', {
        backgroundColor: '#121212',
    })
}))

const StyledQuickAccessSummary = styled(AccordionSummary)(({ theme }) => ({
    display: 'flex',
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    fontSize: '1.25rem'
}))

const StyledListItemLink = styled(ListItem)(({ theme }) => ({
    color: theme.palette.secondary.main,
    cursor: 'pointer',
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.secondary.main,
    cursor: 'pointer',
}))

const AccordionContainer = styled(Box)(() => ({
        marginTop: '1rem'
}))

// const StyledImage = styled('img')(() => ({
//     width: '100%',
//     height: 'auto',
// }))

const StyledImageList = styled(List)(() => ({
    width: '100%',
    height: 'auto',
}))

const StyledImageListItem = styled(ListItem)(() => ({
    width: '100%',
    height: 'auto',
}))

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const CustomAccordion = styled((props: AccordionProps) => (
    <Accordion elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:before': {
        display: 'none',
    }
}));

const CustomToolTip: React.FunctionComponent<{
    label?: string,
    title?: string
}> = ({
    label,
    title
}) => {
        const tip: React.ReactElement = (
            <span style={{ cursor: "help", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {label}
                <sup style={{ fontSize: 10 }}>&#9432;</sup>
            </span >
        );

        return (
            <Tooltip title={title!}>
                {tip}
            </Tooltip>
        );
    }

const CustomAccordionSummary = styled((props: AccordionSummaryProps) => (
    <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faChevronRight} />}
        {...props}
    />
))(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: '2rem',
    flexDirection: 'row-reverse',
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
        color: theme.palette.primary.main,
        fontSize: '1.5rem'
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
        transform: 'rotate(90deg)',
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
    [`& .${typographyClasses.body1}`]: {
        color: theme.palette.primary.main,
        fontSize: '1.5rem'
    }
}));

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const CustomFlexyAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

//const mainPicfTypeId = 1;

const TrialInformationPage: React.FunctionComponent<ITrialInformationPageProps> = () => {
    const isMobile = useIsMobile();

    //const params = useParams();

    //const { enqueueSnackbar } = useSnackbar();

    const [expanded, setExpanded] = React.useState('');

    //@ts-expect-error
    const handleChange = (panel: string) => (event: any, newExpanded: boolean) => {
        setExpanded(newExpanded ? panel : '');
    };

    //const instCode = params?.instCode;

    // const {data: picfs, error: picfError} = usePicfs(instCode)


    // const [mainPicfId, setMainPicfId] = React.useState<number | undefined>();

    // useEffect(() => {
    //     if (picfs && picfs.length > 0) {
    //         setMainPicfId(picfs.find(picf => picf.type.id === mainPicfTypeId)?.picfId);
    //     }
    // }, [picfs, setMainPicfId ]);

    // const picfErrorDisplayed = useRef(false);

    // useEffect(() => {
    //     if (!picfErrorDisplayed.current && picfError) {
    //         picfErrorDisplayed.current = true;

    //         enqueueSnackbar(
    //             <>
    //                 <AlertTitle>
    //                     Error
    //                 </AlertTitle>
    //                 There is a problem with the site PICFs. Please contact your system administrator.
    //             </>,
    //             { variant: 'critical' }
    //         );
    //     }
    // }, [picfErrorDisplayed, picfError, enqueueSnackbar])
    
    // const picfsDownloadLink = `/opms/picf/download/${instCode}/${mainPicfId}/${mainPicfTypeId}`;

    return (
        <PageLayout>
            <PaperContainer>
                <StyledTitle
                    variant="h1"
                >
                    <strong>The OPT-PEMBRO Trial</strong>
                    <br/>
                    <small>BCT 2601 OPT-PEMBRO</small>
                </StyledTitle>
                <br/>
                <Grid2
                    container
                    spacing={5}
                >
                    {
                        !!isMobile && (
                            <Grid2
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12
                                }}
                                sx={{ paddingBottom: 0 }}
                            >
                                <StyledQuickAccess
                                    position="static"
                                    sx={{top: '10rem'}}
                                >
                                    <Accordion>
                                        <StyledQuickAccessSummary
                                            expandIcon={<FontAwesomeIcon icon={faChevronDown} color="white" />}
                                            aria-controls="panel0a-content"
                                            id="panel0a-header"
                                        >
                                            <Typography>Quick Access</Typography>
                                        </StyledQuickAccessSummary>
                                        <AccordionDetails>
                                            <List>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectivePurpose"
                                                >
                                                    <ListItemText primary="What is the purpose of PROSPECTIVE?" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveToDo"
                                                >
                                                    <ListItemText primary="What do I need to do first?" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveInvolvement"
                                                >
                                                    <ListItemText primary="What does taking part in PROSPECTIVE involve?" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveTrialProcedures"
                                                >
                                                    <ListItemText primary="Prospective Trial Procedures" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveQuestionnaires"
                                                >
                                                    <ListItemText primary="Prospective Questionnaires" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveTissueAndImageBanking"
                                                >
                                                    <ListItemText primary="Prospective Tissue & Image Banking" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#PreviousResearch"
                                                >
                                                    <ListItemText primary="Previous Research" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveNextSteps"
                                                >
                                                    <ListItemText primary="Next Steps" />
                                                </StyledListItemLink>
                                                <StyledListItemLink 
                                                    //@ts-expect-error
                                                    component={Link} 
                                                    href="#ProspectiveResources"
                                                >
                                                    <ListItemText primary="Resources" />
                                                </StyledListItemLink>
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                </StyledQuickAccess>
                            </Grid2>
                        )
                    }
                    <Grid2
                        size={{
                            xs: 12,
                            sm: 12,
                            md: isMobile ? 12 :8,
                            lg: isMobile ? 12 :8,
                            xl: isMobile ? 12 :8
                        }}
                    >
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            This page is a summary of the PROSPECTIVE trial and what happens if you decide to take part. Take as much time as you need to decide about taking part. 
                            Talk to your family, mob, friends, and/or doctor about taking part. Feel free to ask any questions of the trial Doctor and site staff at any time. 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            The PROSPECTIVE trial is for people aged 50 years or older, with <CustomToolTip title="Early breast cancer is contained in the breast and may or may not have spread to lymph nodes in the breast or armpit." label="early breast cancer" /> found by standard imaging using a mammogram with or without an ultrasound. 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            To understand more about the PROSPECTIVE trial and what is involved in taking part, click on the headings below to expand the information. 
                            There is also further information on the <StyledLink href={"#ProspectiveResources"}>Resources</StyledLink> section of this page.
                        </Paragraph>
                        <StyledSubtitle
                            variant="h2"
                            id="ProspectiveVideo"
                        >
                            PROSPECTIVE Information Video
                        </StyledSubtitle>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            You may like to take notes of any questions or concerns you have while watching the video or reading this page to ask your trial doctor and trial team. 
                        </Paragraph>

                        <StyledCardMediaContainer id="ProspectivePurpose">
                            <StyledCardMedia
                                //@ts-expect-error
                                component="iframe"
                                src="https://www.youtube.com/embed/yeLeraUUdSs"
                            />
                        </StyledCardMediaContainer>

                        <AccordionContainer>
                            <CustomAccordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} id="ProspectiveToDo">
                                <CustomAccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                                    <Typography>What is the purpose of PROSPECTIVE?</Typography>
                                </CustomAccordionSummary>
                                <CustomAccordionDetails>
                                    <StyledImageList>
                                        <StyledListItemText 
                                            primary={
                                                <>
                                                    PROSPECTIVE will use an imaging test called <CustomToolTip title="A medical imaging device using a strong magnetic field and radio frequency to produce detailed images of internal body parts and structures. MRI is especially useful for imaging soft tissue like the brain, heart, muscles and tumours." label="Magnetic Resonance Imaging (MRI)" /> and 
                                                    look at a sample of people’s breast cancer to select people to not have <CustomToolTip title="The use of radiation, usually x-rays or gamma rays, to kill cancer cells or damage them so they cannot grow and multiply. " label="radiotherapy" /> after breast surgery. That is, they will receive less treatment than usual. 
                                                    This is known as <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/clinical-trial-terminology/" target="_blank" rel="noopener">“treatment optimisation” or “de-escalation”</StyledLink>. 
                                                </>
                                            }
                                        />
                                        <StyledListItemText 
                                            primary={
                                                <>
                                                    The main purpose of PROSPECTIVE is to see if surgery without radiotherapy will still be effective at stopping the cancer coming back while reducing the side effects of treatment. 
                                                </>
                                            }
                                        />
                                        <ListItemText 
                                            primary={
                                                <>
                                                    People taking part in PROSPECTIVE will have an MRI, followed by surgery. The results of the MRI and looking at a sample of the cancer removed at surgery will be used to work out 
                                                    if they don’t need radiotherapy. They will also receive standard treatment for this type of cancer, which may include endocrine (hormone) treatment, chemotherapy or other treatments. 
                                                </>
                                            } 
                                        />
                                    </StyledImageList>
                                </CustomAccordionDetails>
                            </CustomAccordion>
                            <CustomAccordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')} id="ProspectiveInvolvement">
                                <CustomAccordionSummary aria-controls="panel2d-content" id="panel2d-header">
                                    <Typography>What do I need to do first?</Typography>
                                </CustomAccordionSummary>
                                <CustomAccordionDetails>
                                    <StyledImageList>
                                        <StyledListItemText 
                                            primary={
                                                <>
                                                    To take part in the PROSPECTIVE clinical trial, you will need to talk with a trial doctor about the trial, have all your questions answered, 
                                                    and sign the Consent section of the full Participant Information Sheet/Consent Form.  
                                                </>
                                            }
                                        />
                                        <StyledListItemText 
                                            primary={
                                                <>
                                                    You get to decide whether you take part in PROSPECTIVE. You can say yes or no.   
                                                </>
                                            }
                                        />
                                        <ListItemText 
                                            primary={
                                                <>
                                                    If you decide to take part, you can stop at any time if you change your mind later. 
                                                </>
                                            } 
                                        />
                                    </StyledImageList>
                                </CustomAccordionDetails>
                            </CustomAccordion>
                            <CustomAccordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')} id="ProspectiveTrialProcedures">
                                <CustomAccordionSummary aria-controls="panel3d-content" id="panel3d-header">
                                    <Typography>What does taking part in PROSPECTIVE involve?</Typography>
                                </CustomAccordionSummary>
                                <CustomFlexyAccordionDetails>
                                    <StyledImageList>
                                        <StyledImageListItem>
                                            {/* <StyledImage
                                                src={involvement}
                                                alt={"What does taking part in PROSPECTIVE involve?"}
                                            /> */}
                                        </StyledImageListItem>
                                    </StyledImageList>
                                </CustomFlexyAccordionDetails>
                            </CustomAccordion>
                            <CustomAccordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')} id="ProspectiveQuestionnaires">
                                <CustomAccordionSummary aria-controls="panel4d-content" id="panel4d-header">
                                    <Typography>PROSPECTIVE Trial Procedures</Typography>
                                </CustomAccordionSummary>
                                <CustomAccordionDetails>
                                    <StyledImageList>
                                        <StyledImageListItem>
                                            {/* <StyledImage
                                                src={trialprocedures}
                                                alt={"PROSPECTIVE Trial Procedures"}
                                            /> */}
                                        </StyledImageListItem>
                                    </StyledImageList>
                                </CustomAccordionDetails>
                            </CustomAccordion>
                        </AccordionContainer>
                        <StyledSubtitle
                            variant="h2"
                        >
                            PROSPECTIVE Questionnaires – an optional part of the trial 
                        </StyledSubtitle>

                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            Optional means that you can take part in PROSPECTIVE even if you don’t do these questionnaires. We will discuss this with you. 
                        </Paragraph>

                        <div >
                            {/* <StyledImage
                                src={questionnaires}
                                alt={"Questionnaires"}
                            /> */}
                        </div>

                        <Paragraph
                            id="ProspectiveTissueAndImageBanking"
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.uts.edu.au/sites/default/files/2023-06/Understanding%20questionnaires%20in%20cancer%20clinical%20trials_V2_June2023_A4.pdf" target="_blank" rel="noopener">
                                Questionnaires in research projects 
                            </StyledLink>
                        </Paragraph>

                        <StyledSubtitle
                            id="PreviousResearch"
                            variant="h2"
                        >
                            PROSPECTIVE Tissue & Image Banking – an optional part of the trial 
                        </StyledSubtitle>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/tissue-banking/" target="_blank" rel="noopener">Tissue Banking</StyledLink> is a common part of many trials, including PROSPECTIVE. 
                            Tissue banking is donating any tumour tissue or blood samples left over after testing for PROSPECTIVE to a specific Tissue Bank for use in future research. The samples in PROSPECTIVE will be stored at a Tissue Bank in Newcastle, 
                            NSW, Australia. The Tissue Bank must ensure proper standards are met in storing and managing your samples. Any future new studies that use your samples must have appropriate approvals in place (e.g. from a Human Research Ethics Committee) 
                            before your samples can be used. <StyledLink href="https://youtu.be/_uPtwHbHfpk" target="_blank" rel="noopener">What is Tissue Banking?</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            You can also choose to store the images from your MRI for future research. They will be stored on a secure server in Australia. 
                        </Paragraph>

                        <StyledSubtitle
                            variant="h2"
                        >
                            Previous Research
                        </StyledSubtitle>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            PROSPECTIVE is based on a previous trial called PROSPECT. PROSPECT has shown that only 1 of 101 patients with low risk breast cancers seen on MRI scans had that cancer return in the 5 years despite not having had radiotherapy.  
                            This is much lower than expected. 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            You can find further information about the results of PROSPECT at the following links: 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/media-releases/prospect/" target="_blank" rel="noopener">Primary results announced for the PROSPECT Clinical Trial</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/media-releases/prospect-clinical-trial-results/" target="_blank" rel="noopener">PROSPECT Clinical Trial Results</StyledLink>
                        </Paragraph>
                        <Paragraph
                            id="ProspectiveNextSteps"
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/rethinking-radiation-in-early-breast-cancer-prospect-insights/" target="_blank" rel="noopener">Rethinking Radiation in Early Breast Cancer: PROSPECT Trial Insights</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/omitting-radiotherapy-may-improve-quality-of-life-for-patients/" target="_blank" rel="noopener">Omitting radiotherapy may improve quality of life for breast cancer patients</StyledLink>
                        </Paragraph>

                        <StyledSubtitle
                            variant="h2"
                        >
                            Next Steps
                        </StyledSubtitle>
                        {/* <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            Please review the full {mainPicfId ? <StyledLink href={picfsDownloadLink} target="_blank" rel="noopener">Participant Information Sheet/Consent Form</StyledLink> : <>Participant Information Sheet/Consent Form</>} to make sure you have all the information you need to make a decision.
                        </Paragraph> */}
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            The trial doctor and trial team will answer any of your questions or concerns about PROSPECTIVE, and help you understand the trial procedures so you can decide if you want to take part. 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            Please ask as many questions as you would like before agreeing to take part in the PROSPECTIVE trial. You can also withdraw your consent to take part at any time. 
                        </Paragraph>

                        <StyledSubtitle
                            variant="h2"
                            id="ProspectiveResources"
                        >
                            Resources
                        </StyledSubtitle>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            This study is being conducted by <StyledLink href="https://www.breastcancertrials.org.au/home" target="_blank" rel="noopener">Breast Cancer Trials (BCT)</StyledLink>.
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            All trials must be registered on a publicly accessible database. Further information on PROSPECTIVE can be found on the 
                            <StyledLink href="https://clinicaltrials.gov/study/NCT06445738" target="_blank" rel="noopener"> public clinical trials registry.</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href={`${AppSettings.consentParticipantUrl?.replace('trial-consent/participant', 'documents')}/PROSP_Participant Information Summary_v1.0.pdf`} target="_blank" rel="noopener">PROSPECTIVE Trial Summary</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/what-is-a-clinical-trial" target="_blank" rel="noopener">What is a Clinical Trial?</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer" target="_blank" rel="noopener">What is Breast Cancer?</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.youtube.com/watch?v=9wk3PuugnYU&list=PLfaKgjeYDhnV2oWOsnZ9fxtMDQN9KErdQ&index=3" target="_blank" rel="noopener">What is informed Consent?</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/early-stage-breast-cancer/" target="_blank" rel="noopener">Early Stage Breast Cancer</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/clinical-trial-terminology/" target="_blank" rel="noopener">What is De-escalation/Treatment Optimisation?</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.youtube.com/watch?v=b-dmFXvTRSU&t=5s" target="_blank" rel="noopener">Ensuring Patient Confidentiality in a Clinical Trial</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/information-collected-during-a-trial/" target="_blank" rel="noopener">What happens to information collected during a clinical trial? </StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/complaints-and-compensation/" target="_blank" rel="noopener">Complaints and Compensation</StyledLink>
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                        >
                            <StyledLink href="https://www.breastcancertrials.org.au/breast-cancer-resources/translated-brochures/" target="_blank" rel="noopener">Breast Cancer Trials Resources</StyledLink> - We have a number of free brochures available in English, Vietnamese, Korean, Chinese simplified, Chinese Traditional, Arabic, Greek, Te Reo Māori and a dedicated resource for First Nations Australians.
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                            sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}
                        >
                            Ethics approval must be obtained for clinical trials involving human participation. Royal Melbourne Hospital HREC has approved the PROSPECTIVE Trial HREC Reference HREC/112651/MH-2024 under <StyledLink href="https://www.clinicaltrialsandresearch.vic.gov.au/national-mutual-acceptance" target="_blank" rel="noopener">NMA</StyledLink> in Australia.
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                            sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}
                        >
                            The PROSPECTIVE Trial and participant materials have been endorsed by the BCT Consumer Advisory Panel. 
                        </Paragraph>
                        <Paragraph
                            variant="body1"
                            variantMapping={{
                                body1: 'p'
                            }}
                            sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}
                        >
                            Participating in PROSPECTIVE will contribute to clinical research, impacting future clinical research, standard practice for treatment, and future breast cancer patients. 
                        </Paragraph>
                    </Grid2>
                    {
                        !isMobile && 
                        (
                            <Grid2
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 4,
                                    xl: 4
                                }}
                            >
                                <StyledQuickAccess
                                    position="sticky"
                                    sx={{ top: '5rem', }}
                                >
                                    <StyledQuickAccessHeader>
                                        Quick Access
                                    </StyledQuickAccessHeader>
                                    <StyledQuickAccessBody
                                        // sx={{
                                        //     backgroundColor: 'red'
                                        // }}
                                    >
                                        <List>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectivePurpose"
                                            >
                                                <ListItemText primary="What is the purpose of PROSPECTIVE?" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveToDo"
                                            >
                                                <ListItemText primary="What do I need to do first?" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveInvolvement"
                                            >
                                                <ListItemText primary="What does taking part in PROSPECTIVE involve?" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveTrialProcedures"
                                            >
                                                <ListItemText primary="Prospective Trial Procedures" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveQuestionnaires"
                                            >
                                                <ListItemText primary="Prospective Questionnaires" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveTissueAndImageBanking"
                                            >
                                                <ListItemText primary="Prospective Tissue & Image Banking" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#PreviousResearch"
                                            >
                                                <ListItemText primary="Previous Research" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveNextSteps"
                                            >
                                                <ListItemText primary="Next Steps" />
                                            </StyledListItemLink>
                                            <StyledListItemLink 
                                                //@ts-expect-error
                                                component={Link} 
                                                href="#ProspectiveResources"
                                            >
                                                <ListItemText primary="Resources" />
                                            </StyledListItemLink>
                                        </List>
                                    </StyledQuickAccessBody>
                                </StyledQuickAccess>
                            </Grid2>
                        )
                    }
                </Grid2>
            </PaperContainer>
        </PageLayout>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default TrialInformationPage;